import React, { useState } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { stripeAPI } from '@/services/stripeAPI';
import { accountServiceAPI } from '@/services/accountServiceAPI';
import { singleSearchAPI } from '@/services/singleSearchAPI';
import type { SingleSearchRequest } from '@/types';
import { getSearchTypePrice, formatCurrency } from '@/utils/searchValidation';
import { getOneTimeSearchCost } from '@/config/pricing';
import { logger, NoHitError } from '@/utils/logger';
import { Lock, CheckCircle } from 'lucide-react';

interface CustomerInfo {
  name: string;
  email: string;
  companyName: string;
}

interface AIOverview {
  overview: string;
  riskAssessment: string;
  recommendedStrategy: string;
}

interface SearchPaymentCheckoutFormProps {
  searchRequest: SingleSearchRequest;
  searchId: string;
  customerInfo: CustomerInfo;
  savePaymentMethod: boolean;
  onSavePaymentMethodChange: (save: boolean) => void;
  cognitoUserId: string | undefined;
  isAuthenticated: boolean;
  onSuccess: (resultSearchId: string) => void;
  onNoHit: (message: string, aiOverview: AIOverview) => void;
}

export const SearchPaymentCheckoutForm: React.FC<SearchPaymentCheckoutFormProps> = ({
  searchRequest,
  searchId,
  customerInfo,
  savePaymentMethod,
  onSavePaymentMethodChange,
  cognitoUserId,
  isAuthenticated,
  onSuccess,
  onNoHit,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fcraAcknowledgment, setFcraAcknowledgment] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!fcraAcknowledgment) {
      setError('Please acknowledge the FCRA disclaimer before continuing.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    let paymentIntentId: string | undefined;

    try {
      logger.info('Confirming payment for search', { searchId });

      // Confirm the payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (stripeError) {
        logger.error('Stripe payment confirmation failed:', stripeError);
        setError(stripeError.message || 'Payment failed. Please try again.');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'requires_capture') {
        paymentIntentId = paymentIntent.id;
        logger.info('Payment authorized, executing search', { searchId, paymentIntentId });

        // Payment authorized - now execute the search
        const searchResult = await singleSearchAPI.createSearch(searchRequest);

        logger.info('Search completed successfully - determining charge amount', { searchId: searchResult.requestId });

        // Determine actual charge based on what succeeded
        // If scoring_enrich was requested but enrichment failed (no personalInfo), charge for scoring only
        let actualCharge = getSearchTypePrice(searchRequest.searchType);
        let actualSearchType = searchRequest.searchType;

        if (searchRequest.searchType === 'scoring_enrich' && !searchResult.personalInfo) {
          actualCharge = getOneTimeSearchCost('scoring');
          actualSearchType = 'scoring';
          logger.info('Scoring_enrich partial success - charging for scoring only', {
            originalPrice: getSearchTypePrice(searchRequest.searchType),
            actualPrice: actualCharge,
          });
        }

        // Capture the payment (since search was successful and data was found)
        await stripeAPI.capturePayment({
          paymentIntentId: paymentIntent.id,
          actualAmount: actualCharge,
          successfulRows: 1,
          failedRows: 0,
        });

        logger.info('Payment captured successfully', { paymentIntentId: paymentIntent.id, amount: actualCharge, chargedFor: actualSearchType });

        // Save search history to DynamoDB (non-blocking)
        try {
          if (cognitoUserId && customerInfo.email) {
            logger.info('Saving search history to DynamoDB', {
              searchId: searchResult.requestId,
              cognitoUserId,
              email: customerInfo.email
            });

            // Don't await - save in background, don't block navigation
            accountServiceAPI.saveSearchHistory({
              cognitoUserId,
              email: customerInfo.email,
              searchId: searchResult.requestId,
              searchType: actualSearchType, // Use actual search type charged
              searchRequest: {
                firstName: searchRequest.firstName,
                lastName: searchRequest.lastName,
                amountToCollect: searchRequest.amountToCollect,
                address: searchRequest.address,
                city: searchRequest.city,
                state: searchRequest.state,
                zip: searchRequest.zip,
                phone: searchRequest.phone,
              },
              cost: actualCharge, // Use actual charge amount
              hasResults: true,
              firstName: searchRequest.firstName,
              lastName: searchRequest.lastName,
              searchResult, // Include full search result with recovery scenarios
            }).catch((saveError) => {
              // Log but don't block user flow
              logger.error('Failed to save search history (non-blocking):', saveError);
            });
          } else {
            logger.warn('Cannot save search history - missing cognitoUserId or email', {
              hasUserId: !!cognitoUserId,
              hasEmail: !!customerInfo.email
            });
          }
        } catch (historyError) {
          // Log but don't block user flow
          logger.error('Error saving search history (non-blocking):', historyError);
        }

        // Clear sessionStorage
        sessionStorage.removeItem(`search_request_${searchId}`);

        // Navigate to results using the actual search ID from the API
        onSuccess(searchResult.requestId);
      } else {
        logger.error('Unexpected payment intent status:', paymentIntent?.status);
        setError('Payment processing failed. Please try again.');
      }
    } catch (err) {
      // Handle no-hit scenario - cancel payment and show message
      if (err instanceof NoHitError) {
        logger.info('No data found, canceling payment authorization', { searchId, paymentIntentId });

        try {
          // Cancel the payment authorization
          if (paymentIntentId) {
            await stripeAPI.cancelPayment({
              paymentIntentId,
              reason: 'No identity match found',
            });
            logger.info('Payment authorization cancelled - customer not charged', { paymentIntentId });
          }

          // Save no-hit search history to DynamoDB (non-blocking)
          try {
            if (cognitoUserId && customerInfo.email) {
              logger.info('Saving no-hit search history to DynamoDB', {
                searchId,
                cognitoUserId,
                email: customerInfo.email
              });

              // Don't await - save in background
              accountServiceAPI.saveSearchHistory({
                cognitoUserId,
                email: customerInfo.email,
                searchId: searchId || `no_hit_${Date.now()}`,
                searchType: searchRequest.searchType,
                searchRequest: {
                  firstName: searchRequest.firstName,
                  lastName: searchRequest.lastName,
                  amountToCollect: searchRequest.amountToCollect,
                  address: searchRequest.address,
                  city: searchRequest.city,
                  state: searchRequest.state,
                  zip: searchRequest.zip,
                  phone: searchRequest.phone,
                },
                cost: 0, // No charge for no-hit
                hasResults: false,
                firstName: searchRequest.firstName,
                lastName: searchRequest.lastName,
              }).catch((saveError) => {
                logger.error('Failed to save no-hit search history (non-blocking):', saveError);
              });
            }
          } catch (historyError) {
            logger.error('Error saving no-hit search history (non-blocking):', historyError);
          }

          // Clear sessionStorage
          sessionStorage.removeItem(`search_request_${searchId}`);

          // Show no-hit results (with fallback for undefined aiOverview)
          if (err.aiOverview) {
            onNoHit(err.message, err.aiOverview);
          } else {
            // If no aiOverview available, create a default structure
            onNoHit(err.message, {
              overview: 'No identity match found.',
              riskAssessment: 'Unable to assess risk without identity match.',
              recommendedStrategy: 'Verify debtor information and try searching again.',
            });
          }
        } catch (cancelError) {
          logger.error('Failed to cancel payment authorization:', cancelError);
          setError('An error occurred. Please contact support.');
        }
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Payment processing failed';
        logger.error('Payment/search flow failed:', err);
        setError(errorMessage);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const price = getSearchTypePrice(searchRequest.searchType);

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Payment Details</label>
        <PaymentElement />
      </div>

      {/* Save Payment Method Checkbox - only show for authenticated users */}
      {isAuthenticated && (
        <div className="mt-4">
          <label className="flex items-start gap-3 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={savePaymentMethod}
              onChange={(e) => onSavePaymentMethodChange(e.target.checked)}
              className="mt-0.5 w-4 h-4 text-slate-600 border-slate-300 rounded focus:ring-blue-500 flex-shrink-0"
            />
            <span>
              <span className="font-medium text-slate-900 block">Save this payment method for future searches</span>
              <span className="text-xs text-slate-500">Card details stay with Stripe. You can manage saved cards from your dashboard anytime.</span>
            </span>
          </label>
        </div>
      )}

      {/* What You'll Receive */}
      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <h3 className="text-sm font-semibold text-green-900 mb-2 flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          What You'll Receive:
        </h3>
        <ul className="text-sm text-green-800 space-y-1 ml-6 list-disc">
          {searchRequest.searchType === 'validate' && (
            <>
              <li>Identity verification report</li>
              <li>Trust score and ID confidence metrics</li>
              <li>Fraud risk assessment</li>
            </>
          )}
          {searchRequest.searchType === 'enrich' && (
            <>
              <li>Complete contact information</li>
              <li>Phone numbers and email addresses</li>
              <li>Address history</li>
              <li>Relatives and associates</li>
            </>
          )}
          {searchRequest.searchType === 'enrich_validate' && (
            <>
              <li>Identity verification with trust scores</li>
              <li>Complete contact information</li>
              <li>Full skip trace report</li>
              <li>Data quality assessments</li>
            </>
          )}
          {searchRequest.searchType === 'scoring' && (
            <>
              <li>CollectABILITY score and analysis</li>
              <li>Financial insights and metrics</li>
              <li>Recovery scenario recommendations</li>
              <li>Identity verification</li>
            </>
          )}
          {searchRequest.searchType === 'scoring_enrich' && (
            <>
              <li>Complete collectability analysis</li>
              <li>Recovery recommendations</li>
              <li>Complete contact information</li>
              <li>Full financial and skip trace report</li>
            </>
          )}
          <li>Downloadable PDF report</li>
          <li>24-hour access to results</li>
        </ul>
      </div>

      {/* FCRA Acknowledgment */}
      <label className="flex items-start gap-3 text-sm text-slate-600">
        <input
          type="checkbox"
          checked={fcraAcknowledgment}
          onChange={(e) => setFcraAcknowledgment(e.target.checked)}
          className="mt-0.5 w-4 h-4 text-slate-600 border-slate-300 rounded focus:ring-blue-500 flex-shrink-0"
          required
        />
        <span>
          I acknowledge that CollectABILITY is <span className="font-semibold">not a Consumer Reporting Agency (CRA)</span> as defined by the Fair Credit Reporting Act (FCRA), and that I will not use this service for employment, credit, tenant screening, or any FCRA-related purpose.
        </span>
      </label>

      <Button type="submit" disabled={!stripe || isProcessing || !fcraAcknowledgment} size="lg" className="w-full">
        {isProcessing ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            <span>Processing Search & Payment...</span>
          </>
        ) : (
          <>
            <Lock className="w-5 h-5 mr-2" />
            Complete Purchase - {formatCurrency(price)}
          </>
        )}
      </Button>

      {isProcessing && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-slate-900 text-center">
            Please wait while we search and process your payment. This may take a few moments...
          </p>
        </div>
      )}

      <p className="text-xs text-slate-500 text-center">
        By completing this purchase, you agree to our Terms of Service and Privacy Policy
      </p>
    </form>
  );
};
